import {
  ArrowRight,
  Brain,
  FileText,
  Mic,
  Sparkles,
  Target,
  Upload
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [initialCredits, setInitialCredits] = React.useState<number>(2);

  React.useEffect(() => {
    // Fetch config from backend
    // REACT_APP_API_URL should be the base URL (e.g., https://resumeit-production.up.railway.app)
    // The endpoint is /api/config
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    // Remove trailing /api if present to avoid double /api/api
    const baseUrl = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
    fetch(`${baseUrl}/api/config`)
      .then(res => res.json())
      .then(data => setInitialCredits(data.initialCredits || 2))
      .catch(err => {
        console.error('Failed to fetch config:', err);
        // Keep default of 2 if API call fails
      });
  }, []);

  const handleBuildResume = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/auth/google');
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-container">
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">
              ATS-Friendly Resume Builder + Interview Prep
            </h1>
            <p className="hero-subtitle">
              Build ATS-optimized resumes while preparing for interviews. AI-powered questions help you 
              articulate achievements, impact metrics, and technical decisions—so you're ready to ace both ATS filters and interviews.
            </p>
            <div className="hero-actions">
              <button onClick={handleBuildResume} className="btn btn-primary btn-lg">
                {isAuthenticated ? 'Go to Dashboard' : 'Get Started'}
                <ArrowRight size={20} />
              </button>
            </div>
            <p className="hero-note text-tertiary">
              {initialCredits} free resume credits • No credit card required
            </p>
          </div>
        </section>

        <section className="features-section">
          <h2 className="section-title">ATS-Optimized Resumes + Interview Preparation</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Mic size={24} />
              </div>
              <h3>Voice-First Experience</h3>
              <p className="text-secondary">
                Speak naturally to build your resume—faster than typing and perfect for practicing how you'll articulate achievements in interviews.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Brain size={24} />
              </div>
              <h3>Interview-Style Questions</h3>
              <p className="text-secondary">
                AI asks deep-dive questions about impact, metrics, and technical decisions—helping you prepare compelling answers for real interviews.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <FileText size={24} />
              </div>
              <h3>ATS-Optimized Templates</h3>
              <p className="text-secondary">
                Every template passes ATS filters. Get scored and optimize your resume to land more interview opportunities.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Upload size={24} />
              </div>
              <h3>Import Existing</h3>
              <p className="text-secondary">
                Upload your current resume to get started quickly. AI extracts your data automatically.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Target size={24} />
              </div>
              <h3>Tailored Content</h3>
              <p className="text-secondary">
                AI helps you highlight achievements and quantify impact for each role.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <Sparkles size={24} />
              </div>
              <h3>Export Ready</h3>
              <p className="text-secondary">
                Download as PDF. Choose from multiple professional templates.
              </p>
            </div>
          </div>
        </section>

        <section className="process-section">
          <h2 className="section-title">How It Works</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Sign In</h3>
                <p className="text-secondary">Quick and secure authentication with Google</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Share Your Experience</h3>
                <p className="text-secondary">Upload existing resume or build from scratch with voice</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>AI Deep Dive + Interview Prep</h3>
                <p className="text-secondary">Practice articulating impact and technical decisions—perfect preparation for real interviews</p>
              </div>
            </div>
            
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Export & Ace Interviews</h3>
                <p className="text-secondary">Download your ATS-optimized resume, confident and prepared for interviews</p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-content">
            <h2>Ready to Build Your Resume & Ace Interviews?</h2>
            <p className="text-secondary">Join professionals who are beating ATS systems and confidently landing offers</p>
            <button onClick={handleBuildResume} className="btn btn-primary btn-lg">
              {isAuthenticated ? 'Create Resume' : 'Get Started Free'}
              <ArrowRight size={20} />
            </button>
          </div>
        </section>
      </div>

      <footer className="landing-footer">
        <div className="footer-content">
          <p className="text-tertiary">&copy; 2024 ResumeIt. Built to help job seekers succeed.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
